// ─────────────────────────────────────────────────────────────────────────
// FinanceApprovalPanel — the single config-driven finance approval surface for
// all three stages (submissionManager / onboardingManager / cfo) and both
// contract types (W2 / C2C). Presentational + local edit state only: it
// resolves & computes rates via financeApprovalEngine, renders exactly what
// financeApprovalConfig declares, and calls host-provided `actions` — no
// fetching, no routing, no backend URLs (CORE-THEME stays app-agnostic).
//
// Host contract:
//   type            one of FINANCE_TYPE_*
//   record          resolved { submission, job, client, employer, engagement }
//   lookups         { clientVmsFee, locationTaxRows }  (optional, see engine)
//   identity        { name, role, email, phone, location, ...meta }  display-only
//   referralOptions [{ value, label, lastRate, type }]  (onboardingManager)
//   onReferralChange(value) → optionally returns/sets last paid rate
//   currentUserId   stamped into the rate-history audit rows
//   actions         { <api-key>: async ({ computed, proposedRate, referral, reason, category }) => {} }
//   onClose()       popup dismiss (optional)
//   variant         'popup' | 'page'  (sticky action bar vs inline)
// ─────────────────────────────────────────────────────────────────────────
import { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import dayjs from 'dayjs';
import { InputNumber, Select, Input, Checkbox, Tooltip, Modal, message } from 'antd';
import {
  CheckOutlined,
  ClockCircleOutlined,
  LockOutlined,
  WarningOutlined,
  RiseOutlined,
  CloseCircleFilled,
  UserAddOutlined,
} from '@ant-design/icons';
import AppButton from '../AppButton.jsx';
import {
  getFinanceApprovalConfig,
  marginIncludesReferral,
  resolveRejectionReason,
  fillTokens,
  FINANCE_TYPE_CFO,
} from './financeApprovalConfig.js';
import {
  resolveFinanceInputs,
  computeFinance,
  buildRateHistoryEntries,
  formatMoney,
  acceptsKey,
  clampToPolicy,
  isWithinPolicy,
  DEFAULT_NUMERIC_POLICY,
} from './financeApprovalEngine.js';
import { getDefaults } from '../../services/detailDefaults.js';
import './finance-approval.css';

const { TextArea } = Input;

const firstValue = (...values) => values.find(
  (v) => v !== undefined && v !== null && v !== '',
) ?? '-';

// VMS billing cadence label — the Client form stores "onetime"/"recurring",
// the submission's employer VMS type stores "One Time"/"Recurring" (its own
// select options); normalize both to the one display label either can mean.
function vmsTypeLabel(type) {
  return String(type ?? '').toLowerCase().replace(/\s+/g, '') === 'onetime' ? 'One Time' : 'Recurring';
}

function formatSubmittedDate(value) {
  const raw = value?.$date ?? value;
  if (!raw) return '-';
  const parsed = dayjs(raw);
  return parsed.isValid() ? parsed.format(getDefaults().dateFormat) : '-';
}

// ── config-driven value resolution ──────────────────────────────────────────
// The header crumb and the Basic Details grid are declared in
// financeApprovalConfig as { key, label, source: ['record.job.jobTitle', …] }.
// These two helpers are all it takes to render them, and they are the reason no
// field list survives in the JSX below: reordering, adding or dropping a line
// (e.g. dropping Client + Job ID because the crumb already prints them) is a
// config edit with no component change.

// getPath({record, identity, …}, 'record.job.jobTitle') → the value or undefined.
function getPath(ctx, path) {
  return String(path ?? '')
    .split('.')
    .reduce((acc, key) => (acc === null || acc === undefined ? undefined : acc[key]), ctx);
}

// Named value formatters an item can opt into via `format`. Keeping them in a
// registry (rather than letting config carry a function) means the config stays
// serialisable — it can come from the admin API later without change here.
const VALUE_FORMATTERS = { date: formatSubmittedDate };

// resolveConfigValue — first non-empty source path wins, exactly like the
// firstValue() chains that used to be inline in the JSX.
function resolveConfigValue(item, ctx) {
  const paths = Array.isArray(item?.source) ? item.source : [item?.source].filter(Boolean);
  const raw = paths
    .map((path) => getPath(ctx, path))
    .find((v) => v !== undefined && v !== null && v !== '');
  const formatter = VALUE_FORMATTERS[item?.format];
  if (formatter) return formatter(raw);
  return raw === undefined || raw === null || raw === '' ? '-' : raw;
}

// ── numeric input policy → antd props ───────────────────────────────────────
// policyInputProps turns a config policy ({ decimals, min, max, step }) into the
// props that actually enforce it on an antd InputNumber. Three layers, because
// each one alone has a hole:
//   • precision/min/max  — antd's own rounding, applied on blur only
//   • onKeyDown          — refuses the keystroke that would create a 3rd decimal
//                          or a non-numeric character, so the bad value never
//                          appears in the first place
//   • onChange           — clamps (a PASTE bypasses every keystroke guard) and
//                          raises the inline error
// No policy number appears here: decimals:0 in config yields an integer-only
// field, decimals:3 a three-decimal one, with no edit to this file.
function policyInputProps(policy, { onValue, onError }) {
  return {
    min: policy.min,
    max: policy.max,
    step: policy.step,
    precision: policy.decimals,
    // Strip decoration a user may paste ("$62.50", "1,200") down to a number.
    parser: (text) => String(text ?? '').replace(/[^\d.-]/g, ''),
    onKeyDown: (event) => {
      // Never interfere with clipboard/select-all/undo shortcuts — they arrive
      // as ordinary single-character keys ('v', 'a') and would otherwise be
      // rejected as "not a digit".
      if (event.ctrlKey || event.metaKey || event.altKey) return;
      const target = event.target ?? {};
      if (!acceptsKey(policy, event.key, target.value ?? '', target.selectionStart, target.selectionEnd)) {
        event.preventDefault();
        onError(true);
      }
    },
    onChange: (value) => {
      onError(!isWithinPolicy(value, policy));
      onValue(clampToPolicy(value ?? 0, policy));
    },
  };
}

// ── small presentational atoms ───────────────────────────────────────────────

function InfoItem({ label, value }) {
  return (
    <div className="fa-info-item">
      <div className="fa-info-k">{label}</div>
      <div className="fa-info-v">{String(value ?? '-')}</div>
    </div>
  );
}
InfoItem.propTypes = { label: PropTypes.string, value: PropTypes.node };

function RateRow({ label, sub, value, deduction, children, total }) {
  return (
    <div className={`fa-rate-row${total ? ' fa-rate-total' : ''}`}>
      <div className="fa-rate-label">
        {label}
        {sub ? <span className="fa-rate-sub">{sub}</span> : null}
      </div>
      {children ?? (
        <div className={`fa-rate-val fa-num${deduction ? ' fa-deduction' : ''}`}>{value}</div>
      )}
    </div>
  );
}
RateRow.propTypes = {
  label: PropTypes.node,
  sub: PropTypes.node,
  value: PropTypes.node,
  deduction: PropTypes.bool,
  children: PropTypes.node,
  total: PropTypes.bool,
};

function Stat({ label, value, hero, tone }) {
  return (
    <div className={`fa-stat${hero ? ' fa-stat-hero' : ''}${tone ? ` fa-stat-${tone}` : ''}`}>
      <div className="fa-stat-k">{label}</div>
      <div className="fa-stat-v fa-num">{value}</div>
    </div>
  );
}
Stat.propTypes = {
  label: PropTypes.string, value: PropTypes.node, hero: PropTypes.bool, tone: PropTypes.string,
};

// ── main component ───────────────────────────────────────────────────────────

export default function FinanceApprovalPanel({
  type,
  record,
  lookups,
  identity,
  referralOptions,
  onReferralChange,
  onAddReferral,
  currentUserId,
  actions,
  onClose,
  variant,
}) {
  const config = getFinanceApprovalConfig(type);
  // The tail of the engine's currency/rate-unit resolution chains comes from
  // config, so "what do we show when the record says nothing" is admin policy.
  const engineDefaults = useMemo(
    () => ({ rateUnit: config.rateUnit, currency: config.currency }),
    [config],
  );
  const inputs = useMemo(
    () => resolveFinanceInputs(record, lookups, engineDefaults),
    [record, lookups, engineDefaults],
  );
  const showsReferral = marginIncludesReferral(config);

  // ── local edit state ──
  const [proposedRate, setProposedRate] = useState(inputs.proposedRate);
  const [referralId, setReferralId] = useState(
    record?.engagement?.financeApproval?.referralId
    ?? record?.referral?._id ?? null,
  );
  const [referralRate, setReferralRate] = useState(
    record?.engagement?.financeApproval?.referralRate
    ?? record?.referral?.referalRate ?? 0,
  );
  const [historicalReferralRate, setHistoricalReferralRate] = useState(
    record?.referral?.isNewReferral
      ? 0
      : record?.referral?.referalRate
        ?? record?.referral?.referralRate
        ?? record?.engagement?.financeApproval?.referralRate
        ?? 0,
  );
  const [referralType, setReferralType] = useState(
    record?.engagement?.financeApproval?.referralType
    ?? record?.referral?.referralType
    ?? record?.referral?.referalType
    ?? 'Recurring',
  );
  const [proposedRateError, setProposedRateError] = useState(false);
  const [referralRateError, setReferralRateError] = useState(false);
  const [reason, setReason] = useState('');
  const [reasonError, setReasonError] = useState(false);
  const [category, setCategory] = useState('');
  const [categoryError, setCategoryError] = useState(false);
  const [busy, setBusy] = useState('');
  // Preview-first UX (MSI / onboarding review): when the stage config sets
  // rateBreakdown.editToggle, Proposed + Referral render as a locked preview
  // until the approver deliberately clicks Edit. Stages that don't set the
  // flag are unaffected — showRateInputs is always true for them.
  const editToggleCfg = Boolean(config.sections.rateBreakdown?.editToggle);
  const [ratesEditing, setRatesEditing] = useState(false);
  const showRateInputs = !editToggleCfg || ratesEditing;
  // Open state for the "approve above the client rate?" confirm. See runAction
  // for why the *outcome* of that confirm is not stored.
  const [overClientConfirmOpen, setOverClientConfirmOpen] = useState(false);
  const [referralSelectOpen, setReferralSelectOpen] = useState(false);

  const referralCfg = config.sections.referral ?? {};
  // Was a referral actually enabled upstream (the onboarding manager's
  // checkbox)? Persisted on the engagement; falls back to "a rate/ref exists".
  const referralEnabledInitial = record?.engagement?.financeApproval?.referralEnabled
    ?? record?.referralEnabled
    ?? (Number(referralRate) > 0 || Boolean(referralId));
  const [referralEnabled, setReferralEnabled] = useState(Boolean(referralEnabledInitial));

  const hasReferralToggle = referralCfg.toggle === true;
  // Read-only/list finance views should not show a referral name when its
  // effective rate is zero. The onboarding edit screen still uses its own
  // referralEnabled toggle and can keep a selected company while entering a rate.
  const referralExists = Number(referralRate) > 0;
  // show:true      → always (or gated by its own checkbox when toggle:true)
  // show:'ifEnabled'/'ifExists' → only when enabled upstream (cfo)
  const referralVisible = referralCfg.show === true
    ? (hasReferralToggle ? referralEnabled : true)
    : referralCfg.show === 'ifEnabled'
      ? referralEnabled
      : referralCfg.show === 'ifExists'
        ? referralExists
      : false;
  const referralInMargin = showsReferral && referralVisible;
  const rejectionReason = config.rejection?.show ? resolveRejectionReason(config, record) : '';

  // ── live computation ──
  const computed = useMemo(() => computeFinance(inputs, {
    proposedRate: config.sections.rateBreakdown?.proposed?.editable ? proposedRate : undefined,
    includeReferral: referralInMargin,
    referralRate,
  }), [inputs, proposedRate, referralRate, referralInMargin, config]);
  const cur = computed.currency;
  const isReadOnly = config.readOnly;
  const hasProposedRate = Number(computed.proposedRate) > 0;

  // Margin health tone: negative → red (fa-bad); positive but below the
  // configured minimum profit % (default 20%) → orange (fa-warn); at/above the
  // threshold → green (fa-good). Percentage is the margin over the net proposed
  // (client-facing revenue) rate. Threshold is admin-tunable via config.margin.
  const minMarginPercent = Number(config.margin?.minMarginPercent ?? 20);
  const marginPercent = Number(computed.netProposedRate) > 0
    ? (Number(computed.totalMargin) / Number(computed.netProposedRate)) * 100
    : 0;
  const marginTone = !computed.marginPositive
    ? 'fa-bad'
    : marginPercent < minMarginPercent
      ? 'fa-warn'
      : 'fa-good';

  // Employer-side panel label is contract-type aware: a W2 hire's left-hand rate
  // is the CANDIDATE's pay rate; a C2C hire's is the vendor/EMPLOYER rate.
  const employerSideLabel = computed.isW2 ? 'Candidate' : 'Employer';

  // Every rate on this surface is quoted in the record's OWN unit (resolved by
  // the engine: submission → candidate budget → job → config default). Empty
  // when nothing resolves, so an unknown unit prints "$100" and never "$100/".
  const unitSuffix = computed.rateUnitShort ? `/${computed.rateUnitShort}` : '';

  // Config-resolution context for header.crumbFields / sections.jobClient.items.
  const valueContext = { record, identity, inputs, computed };
  const crumbText = (config.header?.crumbFields ?? [])
    .map((field) => resolveConfigValue(field, valueContext))
    .filter((value) => value !== '-' && value !== '' && value !== undefined && value !== null)
    .join(config.header?.crumbSeparator ?? ' · ');

  // Numeric policies for the two money inputs. The referral input deliberately
  // falls back to the proposed rate's policy so one config entry can govern both.
  const proposedPolicy = config.sections.rateBreakdown?.proposed?.policy ?? DEFAULT_NUMERIC_POLICY;
  const referralPolicy = config.sections.referral?.policy ?? proposedPolicy;

  // Employer VMS wording — three distinct states, all labelled from config:
  // it applies (deduction row), the employer HAS a VMS % but this submission
  // didn't require it, or the employer has no VMS % configured at all.
  const vmsLabels = config.labels?.employerVms ?? {};
  const employerVmsApplies = computed.employerVmsRequired && computed.employerVmsAvailable;

  // ── proposed rate vs the Client Job Rate ──
  // Being ABOVE the client's posted rate is a commercial judgement, not an
  // error: the approver may knowingly ask for more. So it produces a WARNING
  // (with the full comparison, and a deliberate confirm step) rather than a
  // dead button. `overClientPolicy` can restore the old hard block, or silence
  // it entirely, without touching this file.
  const proposedEditable = Boolean(config.sections.rateBreakdown?.proposed?.editable);
  const clientJobRate = Number(computed.clientJobRate) || 0;
  const proposedOverClient = proposedEditable && clientJobRate > 0 && Number(proposedRate) > clientJobRate;

  const overClientCfg = config.sections.rateBreakdown?.proposed?.overClient ?? {};
  const overClientPolicy = config.sections.rateBreakdown?.proposed?.overClientPolicy ?? 'warn';
  const overClientBlocks = proposedOverClient && overClientPolicy === 'block';
  const overClientWarns = proposedOverClient && overClientPolicy === 'warn';

  // The numbers behind the warning, resolved once so the panel, the confirm
  // dialog and the tooltip all quote exactly the same figures.
  const overClientDelta = proposedOverClient ? Number(proposedRate) - clientJobRate : 0;
  const overClientPct = clientJobRate > 0 ? (overClientDelta / clientJobRate) * 100 : 0;
  const overClientTokens = {
    proposed: `${formatMoney(Number(proposedRate), cur)}${unitSuffix}`,
    client: `${formatMoney(clientJobRate, cur)}${unitSuffix}`,
    delta: `${formatMoney(overClientDelta, cur)}${unitSuffix}`,
    pct: `${overClientPct.toFixed(overClientPct < 10 ? 1 : 0)}%`,
    unit: computed.rateUnitShort ?? '',
  };
  const overClientText = (key) => fillTokens(overClientCfg[key], overClientTokens);

  // ── primary-action gate ──
  // Stages whose primary action declares `requireProposedRate` (Approve /
  // Send For Approval) cannot run until a real proposed rate is entered — it
  // is what the margin, the CFO request and payroll are all derived from.
  // `primaryBlockedReason` doubles as the disabled-button hover explanation:
  // a disabled button with no stated cause is the thing users get stuck on.
  const primaryAction = config.actions?.primary;
  const proposedRateMissing = Boolean(primaryAction?.requireProposedRate)
    && !(Number(computed.proposedRate) > 0);
  const primaryBlockedReason = proposedRateMissing
    ? 'Enter the Proposed Rate first — it drives the margin and the rate sent for approval.'
    : overClientBlocks
      ? overClientText('blockMessage')
      : '';

  const handleReferralPick = (value) => {
    setReferralId(value);
    setReferralSelectOpen(false);
    const picked = (referralOptions ?? []).find((o) => o.value === value);
    if (!picked?.isNewReferral && picked?.lastRate != null && picked.lastRate !== '') {
      const pickedLastRate = Number(picked.lastRate) || 0;
      setReferralRate(pickedLastRate);
      setHistoricalReferralRate(pickedLastRate);
    } else {
      setHistoricalReferralRate(0);
    }
    if (picked?.type) setReferralType(picked.type);
    onReferralChange?.(value, picked);
  };

  const handleReferralEnabledChange = (event) => {
    const checked = event.target.checked;
    setReferralEnabled(checked);
    setReferralSelectOpen(checked);
  };

  const referralDropdownRender = (menu) => (
    <div>
      {menu}
      {onAddReferral && (
        <div className="fa-referral-dropdown-footer">
          <AppButton
            className="fa-referral-add-btn"
            icon={<UserAddOutlined />}
            onMouseDown={(event) => event.preventDefault()}
            onClick={() => {
              setReferralSelectOpen(false);
              onAddReferral();
            }}
          >
            Add
          </AppButton>
        </div>
      )}
    </div>
  );

  // `skipOverClientConfirm` is passed only by the confirm dialog's own OK
  // handler. Deliberately NOT a piece of state: a "user already confirmed"
  // flag would survive a subsequent edit of the proposed rate, so a second,
  // higher rate could be approved on the strength of confirming the first.
  const runAction = async (actionCfg, { skipOverClientConfirm = false } = {}) => {
    if (!actionCfg) return;
    // Only genuine blockers stop the action here — today that is a missing
    // proposed rate (and an over-client rate ONLY when config says 'block').
    // Re-checked outside the button's disabled attribute so a programmatic or
    // keyboard-triggered call can't slip past it.
    if (actionCfg === config.actions.primary && primaryBlockedReason) {
      message.error(primaryBlockedReason);
      return;
    }
    // Over the client rate under the default 'warn' policy: approval is
    // allowed, but it must be deliberate. The confirm restates the exact
    // comparison so nobody approves a premium rate by muscle memory.
    if (actionCfg === config.actions.primary && overClientWarns && !skipOverClientConfirm) {
      setOverClientConfirmOpen(true);
      return;
    }
    if (actionCfg.requireReason && !reason.trim()) {
      setReasonError(true);
      return;
    }
    if (actionCfg.requireCategory && !category) {
      setCategoryError(true);
      return;
    }
    const handler = actions?.[actionCfg.api];
    if (!handler) {
      message.error(`No handler wired for "${actionCfg.api}"`);
      return;
    }
    setBusy(actionCfg.key);
    try {
      await handler({
        computed,
        proposedRate: computed.proposedRate,
        referral: referralVisible
          ? {
            referralId, referralRate: computed.referralRate, referralType, referralEnabled: true,
          }
          : { referralEnabled: false, referralRate: 0 },
        referralEnabled,
        reason: reason.trim(),
        category,
        rateHistory: config.persist?.rateHistory
          ? buildRateHistoryEntries(computed, currentUserId)
          : undefined,
      });
    } catch (error) {
      message.error(error?.message ?? 'Action failed');
    } finally {
      setBusy('');
    }
  };

  const primary = config.actions.primary;
  const reject = config.actions.reject;

  return (
    <div className={`fa-panel fa-variant-${variant}`}>
      {/* Header */}
      {config.header?.show !== false && (
        <div className="fa-topbar">
          <div>
            <div className="fa-crumb">{crumbText}</div>
            <div className="fa-title-row">
              <h2 className="fa-title">{config.title} — {firstValue(identity?.name, record?.submission?.candidateFullName)}</h2>
              <span className="fa-status-pill">
                <ClockCircleOutlined /> {config.statusLabel}
              </span>
            </div>
          </div>
        </div>
      )}

      {/* CFO rejection banner (re-approval stages) */}
      {config.rejection?.show && (
        <div className="fa-rejection">
          <CloseCircleFilled className="fa-rejection-icon" />
          <div className="fa-rejection-body">
            <div className="fa-rejection-title">{config.rejection.title}</div>
            <div className={`fa-rejection-reason${rejectionReason ? '' : ' fa-rejection-empty'}`}>
              {rejectionReason || 'No reason was provided.'}
            </div>
          </div>
        </div>
      )}

      {/* Approval trail */}
      {config.trail?.show && (
        <div className="fa-trail">
          {config.trail.steps.map((step, i) => (
            <span key={step.key} className="fa-trail-part">
              <span className={`fa-trail-step${step.key === config.trail.current ? ' fa-current' : ''}`}>
                {step.key === config.trail.current ? <ClockCircleOutlined /> : <CheckOutlined />}
                {step.label}
              </span>
              {i < config.trail.steps.length - 1 && <span className="fa-trail-sep">→</span>}
            </span>
          ))}
        </div>
      )}

      {/* Job & Client details */}
      {config.sections.jobClient?.show && (
        <section className="fa-card">
          <div className="fa-card-head"><h3>{config.sections.jobClient.title}</h3></div>
          <div className="fa-card-body">
            {/* Entirely config-driven (sections.jobClient.items). Client and
                Job ID are intentionally NOT in the default item list — the
                header crumb already prints both, and having them here too was
                the duplication QA reported. */}
            <div className="fa-info-grid">
              {(config.sections.jobClient.items ?? []).map((item) => (
                <InfoItem key={item.key} label={item.label} value={resolveConfigValue(item, valueContext)} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Client Job Rate (context strip) */}
      {config.sections.clientJobRate?.show && (
        <section className="fa-card">
          <div className="fa-card-head"><h3>Client Job Rate</h3></div>
          <div className="fa-card-body">
            <div className="fa-strip">
              <div className="fa-strip-item">
                <span className="fa-strip-k">Client Job Rate</span>
                <span className="fa-strip-v fa-num">{formatMoney(computed.clientJobRate, cur)}{unitSuffix}</span>
              </div>
              <span className="fa-strip-op fa-num">
                − {computed.clientVmsPercent}% VMS
                <span className="fa-type-chip fa-vms-chip">{vmsTypeLabel(computed.clientVmsPercentageType)}</span>
              </span>
              <span className="fa-strip-eq">=</span>
              <div className="fa-strip-item">
                <span className="fa-strip-k">Net Client Rate</span>
                <span className="fa-strip-v fa-num" style={{ color: 'var(--color-brand, #0053a5)' }}>
                  {formatMoney(computed.netClientRate, cur)}{unitSuffix}
                </span>
              </div>
              {config.sections.clientJobRate.note && (
                <div className="fa-strip-note">
                  For context only — the client-side rate doesn&apos;t feed into the {config.margin.label} below.
                </div>
              )}
            </div>
          </div>
        </section>
      )}

      {/* Rate Breakdown */}
      {config.sections.rateBreakdown?.show && (
        <section className="fa-card">
          <div className="fa-card-head">
            <h3>Rate Breakdown</h3>
            {config.sections.rateBreakdown.finalBadge && (
              <span className="fa-final-badge"><LockOutlined /> Final</span>
            )}
            {editToggleCfg && !isReadOnly && !ratesEditing && (
              <AppButton
                className="fa-rate-edit-btn"
                size="small"
                onClick={() => setRatesEditing(true)}
              >
                Edit
              </AppButton>
            )}
          </div>
          <div className="fa-card-body">
            <div className="fa-rate-grid">
              {/* Employer side */}
              <div className="fa-rate-panel fa-employer">
                <div className="fa-panel-head"><span className="fa-dot" />{employerSideLabel} Rate</div>
                <div className="fa-rate-rows">
                  <RateRow label={`${employerSideLabel} Rate`} sub="as submitted by recruiter"
                    value={<>{formatMoney(computed.employerRate, cur)} <span className="fa-unit">{unitSuffix}</span></>} />
                  {computed.isW2 && (
                    <RateRow label="Employer Location" sub="used to determine location tax">
                      <div className="fa-rate-val">{firstValue(identity?.employerLocation, record?.submission?.city, record?.job?.jobLocation)}</div>
                    </RateRow>
                  )}
                  {!computed.isW2 && !employerVmsApplies ? (
                    // C2C with no employer-side VMS deduction — but WHY there is
                    // none matters to the approver, and a single "Not required"
                    // hid the difference: an employer that HAS a VMS % where the
                    // recruiter unchecked "VMS Required" is a decision, while an
                    // employer with no VMS % configured at all is missing data.
                    // Both labels come from config.labels.employerVms.
                    <RateRow
                      label={<span className="fa-locked-label">{vmsLabels.required}</span>}
                      sub={computed.employerVmsAvailable ? vmsLabels.notRequiredSub : vmsLabels.unavailableSub}
                      value={(
                        <span className="fa-vms-none">
                          {computed.employerVmsAvailable ? vmsLabels.notRequired : vmsLabels.unavailable}
                        </span>
                      )}
                    />
                  ) : (
                    <RateRow
                      label={!computed.isW2 ? (
                        <span className="fa-locked-label">
                          {computed.employerDeductionLabel}
                          <span className="fa-type-chip fa-vms-chip">{vmsTypeLabel(computed.employerVmsType)}</span>
                        </span>
                      ) : computed.employerDeductionLabel}
                      sub={`${computed.employerDeductionPercent}% of employer rate`}
                      value={`−${formatMoney(computed.employerDeduction, cur).replace('−', '')}`}
                      deduction
                    />
                  )}
                  <RateRow total label={`Net ${employerSideLabel} Rate`}
                    value={<span style={{ color: 'var(--color-purple, #7c3aed)' }}>{formatMoney(computed.netEmployerRate, cur)}</span>} />
                </div>
              </div>

              {/* Proposed side */}
              <div className="fa-rate-panel fa-client">
                <div className="fa-panel-head"><span className="fa-dot" />Proposed Rate</div>
                <div className="fa-rate-rows">
                  {config.sections.rateBreakdown.proposed.editable && showRateInputs ? (
                    <div className="fa-rate-row fa-editable">
                      <div className="fa-editable-head">
                        <span className="fa-editable-label">
                          Proposed Rate
                          <span className="fa-edit-badge">Editable</span>
                        </span>
                        <InputNumber
                          className="fa-rate-input"
                          {...policyInputProps(proposedPolicy, {
                            onValue: setProposedRate,
                            onError: setProposedRateError,
                          })}
                          value={proposedRate}
                          prefix={cur}
                          controls={false}
                          // An over-client rate is no longer an error state —
                          // under the default 'warn' policy the input is
                          // flagged amber, not red, because the value is
                          // acceptable and only wants a second look.
                          status={proposedRateError || overClientBlocks
                            ? 'error'
                            : overClientWarns ? 'warning' : ''}
                        />
                      </div>
                      <div className="fa-editable-sub">rate we ask the client for</div>
                      {proposedRateError && (
                        <div className="fa-reason-error">! {proposedPolicy.message}</div>
                      )}
                      {proposedRateMissing && (
                        <div className="fa-reason-error">
                          ! Enter the proposed rate to enable {primaryAction?.label ?? 'approval'}.
                        </div>
                      )}
                      {overClientBlocks && (
                        <div className="fa-reason-error">! {overClientText('blockMessage')}</div>
                      )}
                      {/* Re-approval only: the rate that was on the record when the
                          CFO rejected it (inputs.proposedRate is captured once at
                          mount, before any edit here) — a quiet reminder of what
                          didn't work while the manager picks a new number. */}
                      {config.rejection?.show && (
                        <div className="fa-last-rejected">
                          Last rejected at <b>{formatMoney(inputs.proposedRate, cur)}{unitSuffix}</b>
                        </div>
                      )}
                    </div>
                  ) : (
                    <RateRow
                      label={(
                        <span className="fa-locked-label">
                          Proposed Rate
                          {config.sections.rateBreakdown.proposed.locked && (
                            <span className="fa-locked-badge"><LockOutlined /> Locked</span>
                          )}
                        </span>
                      )}
                      sub="approved at finance review"
                      value={<>{formatMoney(computed.proposedRate, cur)} <span className="fa-unit">{unitSuffix}</span></>}
                    />
                  )}
                  <RateRow
                    label={(
                      <span className="fa-locked-label">
                        Discount (VMS)
                        <span className="fa-type-chip fa-vms-chip">{vmsTypeLabel(computed.clientVmsPercentageType)}</span>
                      </span>
                    )}
                    sub={`${computed.clientVmsPercent}% of proposed rate`}
                    value={`−${formatMoney(computed.discountVms, cur).replace('−', '')}`} deduction />
                  <RateRow total label="Net Proposed Rate"
                    value={<span style={{ color: 'var(--color-brand, #0053a5)' }}>{formatMoney(computed.netProposedRate, cur)}</span>} />
                </div>
              </div>
            </div>

            {/* "Add Referral" gate — onboarding manager decides whether a
                referral applies; downstream (CFO) only sees it if enabled. */}
            {hasReferralToggle && !isReadOnly && showRateInputs && (
              <div className="fa-referral-toggle">
                <Checkbox
                  checked={referralEnabled}
                  onChange={handleReferralEnabledChange}
                >
                  <span className="fa-rt-title">{referralCfg.toggleLabel ?? 'Add Referral'}</span>
                </Checkbox>
                <div className="fa-rt-hint">{referralCfg.toggleHint}</div>
              </div>
            )}
          </div>
        </section>
      )}

      {/* Referral */}
      {referralVisible && (
        <section className="fa-card fa-referral-card">
          <div className="fa-card-head">
            <h3><span className="fa-dot fa-dot-warning" />Referral</h3>
            {referralCfg.mode === 'print' && referralCfg.typeChip && (
              <span className="fa-type-chip">{referralType}</span>
            )}
          </div>
          <div className="fa-card-body">
            {referralCfg.mode === 'select' && showRateInputs ? (
              <div className="fa-referral-fields">
                <div className="fa-referral-field">
                  <label className="fa-rf-k" htmlFor="fa-referral-company">Referral Company</label>
                  <Select
                    id="fa-referral-company"
                    className="fa-referral-select"
                    placeholder="Select a referral"
                    open={referralSelectOpen}
                    value={referralId ?? undefined}
                    onChange={handleReferralPick}
                    options={referralOptions ?? []}
                    showSearch
                    optionFilterProp="label"
                    onDropdownVisibleChange={setReferralSelectOpen}
                    onOpenChange={setReferralSelectOpen}
                    popupRender={referralDropdownRender}
                    dropdownRender={referralDropdownRender}
                  />
                </div>
                <div className="fa-referral-field">
                  <label className="fa-rf-k" htmlFor="fa-referral-rate">Referral Rate</label>
                  <InputNumber
                    id="fa-referral-rate"
                    className="fa-referral-rate-input"
                    {...policyInputProps(referralPolicy, {
                      onValue: setReferralRate,
                      onError: setReferralRateError,
                    })}
                    value={referralRate}
                    prefix={cur}
                    controls={false}
                    disabled={!referralId}
                    status={referralRateError ? 'error' : ''}
                  />
                  {referralRateError && (
                    <div className="fa-reason-error">! {referralPolicy.message}</div>
                  )}
                </div>
                <div className="fa-referral-field">
                  <label className="fa-rf-k" htmlFor="fa-referral-type">Referral Type</label>
                  <Select
                    id="fa-referral-type"
                    className="fa-referral-type"
                    value={referralType}
                    onChange={setReferralType}
                    disabled={!referralId}
                    options={[
                      { value: 'Recurring', label: 'Recurring — every pay cycle' },
                      { value: 'One-time', label: 'One-time payout' },
                    ]}
                  />
                </div>
                {referralId && (
                  <div className="fa-referral-note">
                    {historicalReferralRate ? (
                      <>
                        Last referral rate paid to this company:{' '}
                        <b>{formatMoney(historicalReferralRate, cur)}</b>
                      </>
                    ) : (
                      <b>New referral</b>
                    )}
                  </div>
                )}
              </div>
            ) : (
              <div className="fa-rate-rows fa-print-rows">
                <RateRow label="Referral Name" sub="added during onboarding">
                  <div className="fa-rate-val">{firstValue(record?.referral?.referalCompanyName, identity?.referralName)}</div>
                </RateRow>
                <RateRow
                  label="Referral Rate"
                  sub={referralType === 'One-time' ? 'deducted once, this payout only' : 'deducted every pay cycle'}
                  value={`−${formatMoney(referralRate, cur).replace('−', '')}${unitSuffix ? ` ${unitSuffix}` : ''}`}
                  deduction
                />
              </div>
            )}
          </div>
        </section>
      )}

      {/* Outcome — the margin */}
      {hasProposedRate && (
        <div className={`fa-outcome ${marginTone}`}>
          <div className="fa-outcome-label">
            <div className="fa-outcome-icon">
              {marginTone === 'fa-good' ? <RiseOutlined /> : <WarningOutlined />}
            </div>
            <div className="fa-outcome-t">{config.margin.label}</div>
          </div>
          <div className="fa-outcome-stats">
            <Stat label="Net Proposed Rate" value={<>{formatMoney(computed.netProposedRate, cur)}<span className="fa-u">{unitSuffix}</span></>} />
            <span className="fa-stat-arrow">−</span>
            <Stat label={`Net ${employerSideLabel} Rate`} value={<>{formatMoney(computed.netEmployerRate, cur)}<span className="fa-u">{unitSuffix}</span></>} />
            {referralInMargin && (
              <>
                <span className="fa-stat-arrow">−</span>
                <Stat label="Referral Rate" tone="referral" value={<>{formatMoney(computed.referralRate, cur)}<span className="fa-u">{unitSuffix}</span></>} />
              </>
            )}
            <Stat hero label="Margin" value={<>{formatMoney(computed.totalMargin, cur)}<span className="fa-u">{unitSuffix}</span></>} />
          </div>
        </div>
      )}

      {/* Margin health message — a plain-language read on the profit % against
          the configured target, coloured to match the outcome tone. */}
      {hasProposedRate && (
        <div className={`fa-margin-note ${marginTone}`}>
          <span className="fa-margin-note-pct">{marginPercent.toFixed(1)}%</span>
          <span className="fa-margin-note-text">
            {marginTone === 'fa-bad'
              ? <>This submission is running at a <b>loss</b>. Adjust the proposed or employer rate before approving.</>
              : marginTone === 'fa-warn'
                ? <>Profit margin is <b>below the {minMarginPercent}% target</b>. Consider a higher proposed rate before approving.</>
                : <>Healthy margin — at or above the {minMarginPercent}% target.</>}
          </span>
        </div>
      )}

      {/* Reject reason (only when the reject action requires one and it's not read-only-CFO-approved) */}
      {reject?.requireReason && (
        <div className="fa-reason">
          {reject.requireCategory && (
            <>
              <label className="fa-reason-label" htmlFor="fa-rejection-category">
                Return To <span>(required to reject)</span>
              </label>
              <Select
                id="fa-rejection-category"
                placeholder="Select where to return this rejection"
                value={category || undefined}
                status={categoryError ? 'error' : ''}
                options={[
                  { value: 'submission', label: 'Submission' },
                  { value: 'onboarding', label: 'Onboarding' },
                ]}
                onChange={(value) => { setCategory(value); setCategoryError(false); }}
                style={{ width: '100%', marginBottom: 16 }}
              />
              {categoryError && <div className="fa-reason-error">! Select where to return the rejection.</div>}
            </>
          )}
          <label className="fa-reason-label" htmlFor="fa-reason">
            Comments <span>(required to reject)</span>
          </label>
          <TextArea
            id="fa-reason"
            rows={3}
            maxLength={500}
            showCount
            value={reason}
            status={reasonError ? 'error' : ''}
            placeholder="Enter your comments here…"
            onChange={(e) => { setReason(e.target.value); if (e.target.value.trim()) setReasonError(false); }}
          />
          {reasonError && <div className="fa-reason-error">! Comments are required when rejecting.</div>}
        </div>
      )}

      {/* Over-the-client-rate warning — the full comparison, immediately above
          the action bar so it is read at the moment of deciding rather than
          discovered as a disabled button. Every string is config. */}
      {overClientWarns && (
        <div className="fa-rate-warning" role="status">
          <div className="fa-rate-warning-head">
            <WarningOutlined className="fa-rate-warning-icon" />
            <h4>{overClientText('title')}</h4>
          </div>
          <div className="fa-rate-warning-grid">
            <div className="fa-rate-warning-cell">
              <span className="fa-rate-warning-key">Client Job Rate</span>
              <span className="fa-rate-warning-val">{overClientTokens.client}</span>
            </div>
            <div className="fa-rate-warning-cell">
              <span className="fa-rate-warning-key">Proposed Rate</span>
              <span className="fa-rate-warning-val">{overClientTokens.proposed}</span>
            </div>
            <div className="fa-rate-warning-cell fa-rate-warning-delta">
              <span className="fa-rate-warning-key">Over by</span>
              <span className="fa-rate-warning-val">
                {overClientTokens.delta} <small>({overClientTokens.pct})</small>
              </span>
            </div>
          </div>
          {overClientText('body') && (
            <p className="fa-rate-warning-body">{overClientText('body')}</p>
          )}
          {/* The resulting margin, in the same language the margin tile uses —
              the consequence the approver actually needs in order to judge. */}
          <p className="fa-rate-warning-margin">
            Resulting margin:{' '}
            <b className={marginTone}>
              {formatMoney(computed.totalMargin, cur)}{unitSuffix}
              {Number(computed.netProposedRate) > 0 && <> ({marginPercent.toFixed(1)}%)</>}
            </b>
          </p>
        </div>
      )}

      {/* Deliberate second step for approving above the client rate. */}
      <Modal
        open={overClientConfirmOpen}
        title={overClientText('confirmTitle')}
        okText={overClientText('confirmOk') || 'Approve anyway'}
        cancelText={overClientText('confirmCancel') || 'Go back'}
        okButtonProps={{ danger: true }}
        onCancel={() => setOverClientConfirmOpen(false)}
        onOk={() => {
          setOverClientConfirmOpen(false);
          runAction(config.actions.primary, { skipOverClientConfirm: true });
        }}
      >
        <p>{overClientText('confirmBody')}</p>
      </Modal>

      {/* Action bar */}
      {config.actions?.show !== false && (primary || reject) && (
        <div className="fa-action-bar">
          <div className="fa-action-note">
            {proposedRateMissing
              ? <><b>Proposed rate required</b> — enter it above to enable {primary?.label ?? 'approval'}.</>
              : type === FINANCE_TYPE_CFO
              ? <>Approving finalizes payroll at <b>{formatMoney(computed.netProposedRate, cur)}{unitSuffix}</b>{referralVisible ? <> including the {String(referralType).toLowerCase()} <b>{formatMoney(referralRate, cur)}{unitSuffix}</b> referral</> : null}.</>
              : <>Approving locks the <b>{formatMoney(computed.netProposedRate, cur)}{unitSuffix}</b> proposed rate and moves this forward.</>}
          </div>
          <div className="fa-actions">
            {onClose && <AppButton className="fa-btn-ghost" onClick={onClose}>Cancel</AppButton>}
            {reject && (
              <AppButton
                danger
                loading={busy === reject.key}
                disabled={Boolean(busy)}
                onClick={() => runAction(reject)}
              >
                {reject.label}
              </AppButton>
            )}
            {primary && (
              // A disabled antd Button swallows mouse events, so the Tooltip is
              // anchored on a wrapper span — otherwise the "why is this off?"
              // explanation never appears, which is the whole point here.
              <Tooltip title={primaryBlockedReason || ''} placement="top">
                <span className="fa-btn-tip">
                  <AppButton
                    variant="primary"
                    icon={<CheckOutlined />}
                    loading={busy === primary.key}
                    disabled={Boolean(busy) || Boolean(primaryBlockedReason)}
                    onClick={() => runAction(primary)}
                  >
                    {primary.label}
                  </AppButton>
                </span>
              </Tooltip>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

FinanceApprovalPanel.propTypes = {
  type: PropTypes.string.isRequired,
  record: PropTypes.object,
  lookups: PropTypes.object,
  identity: PropTypes.object,
  referralOptions: PropTypes.array,
  onReferralChange: PropTypes.func,
  onAddReferral: PropTypes.func,
  currentUserId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  actions: PropTypes.object,
  onClose: PropTypes.func,
  variant: PropTypes.oneOf(['popup', 'page']),
};

FinanceApprovalPanel.defaultProps = {
  record: {},
  lookups: {},
  identity: {},
  referralOptions: [],
  variant: 'page',
};
